import React from 'react';
import { DetailsForm, AINEDetailsForm, DINEDetailsForm } from '../../DetailsForm';
import { PolicyTemplate, ModifyDetails, AINEDetails, DINEDetails, ConflictEffect } from '../../../types/policy';
import { Operation as DetailsFormOperation } from '../../DetailsForm/types';
import { Operation as PolicyOperation } from '../../../types/policy';

/**
 * Convert policy operations to DetailsForm operations
 */
const convertPolicyOperationsToDetailsForm = (operations: PolicyOperation[]): DetailsFormOperation[] => {
  return operations.map(op => {
    const result: DetailsFormOperation = {
      operation: op.operation,
      field: op.field,
      value: op.value ? (Array.isArray(op.value) ? op.value as Record<string, any>[] : String(op.value)) : ''
    };
    
    // Only include condition if it has a value
    if (op.condition && op.condition.trim()) {
      result.condition = op.condition;
    }
    
    return result;
  });
};

/**
 * Convert DetailsForm operations back to policy operations
 */
const convertDetailsFormOperationsToPolicy = (operations: DetailsFormOperation[]): PolicyOperation[] => {
  return operations.map(op => {
    const result: PolicyOperation = {
      operation: op.operation as any, // DetailsForm uses string, policy uses ModifyOperation
      field: op.field,
      value: op.value
    };
    
    // Only include condition if it has a value
    if (op.condition && op.condition.trim()) {
      result.condition = op.condition;
    }
    
    return result;
  });
};

/**
 * Convert ConflictEffect to string
 */
const convertConflictEffectToString = (effect?: ConflictEffect): string => {
  return effect || '';
};

/**
 * Convert string to ConflictEffect
 */
const convertStringToConflictEffect = (effect: string): ConflictEffect | undefined => {
  if (effect === 'audit' || effect === 'deny' || effect === 'disabled') {
    return effect as ConflictEffect;
  }
  return undefined;
};

/**
 * Props interface for the DetailsTab component
 * @property policy - The current policy template being edited
 * @property selectedEffect - Currently selected policy effect
 * @property onUpdateDetails - Callback function to handle updates to policy details
 */
interface DetailsTabProps {
  policy: PolicyTemplate;
  selectedEffect: string;
  onUpdateDetails: (details: ModifyDetails | AINEDetails | DINEDetails) => void;
}

/**
 * Type guard to check if details are ModifyDetails
 */
const isModifyDetails = (details: ModifyDetails | AINEDetails | DINEDetails | undefined): details is ModifyDetails => {
  return details !== undefined && 'operations' in details;
};

/**
 * Type guard to check if details are AINEDetails
 */
const isAINEDetails = (details: ModifyDetails | AINEDetails | DINEDetails | undefined): details is AINEDetails => {
  return details !== undefined && 'type' in details && !('deployment' in details);
};

/**
 * Type guard to check if details are DINEDetails
 */
const isDINEDetails = (details: ModifyDetails | AINEDetails | DINEDetails | undefined): details is DINEDetails => {
  return details !== undefined && 'deployment' in details;
};

/**
 * DetailsTab component for managing policy details configuration
 * 
 * This component serves as a container for effect-specific detail forms:
 * - For 'modify' effect: Shows role assignments, conflict resolution, and operations
 * - For 'auditIfNotExists' effect: Shows AINE-specific configuration (placeholder for now)
 * 
 * It extracts the relevant details from the policy template and passes them
 * to the appropriate form component for editing.
 * 
 * @example
 * <DetailsTab
 *   policy={currentPolicy}
 *   selectedEffect="modify"
 *   onUpdateDetails={handleDetailsUpdate}
 * />
 */
export const DetailsTab: React.FC<DetailsTabProps> = ({
  policy,
  selectedEffect,
  onUpdateDetails
}) => {
  const details = policy.properties.policyRule.then.details;

  // Handle modify effect
  if (selectedEffect.toLowerCase() === 'modify') {
    const handleModifyUpdate = (formDetails: {
      roleDefinitionIds: string[];
      conflictEffect?: string;
      operations: DetailsFormOperation[];
    }) => {
      // Convert conflict effect first to determine if it should be included
      const convertedConflictEffect = convertStringToConflictEffect(formDetails.conflictEffect || '');
      
      // Construct ModifyDetails object with proper property ordering
      // This ensures conflictEffect appears after roleDefinitionIds but before operations
      const modifyDetails: ModifyDetails = {
        roleDefinitionIds: formDetails.roleDefinitionIds,
        ...(convertedConflictEffect && { conflictEffect: convertedConflictEffect }),
        operations: convertDetailsFormOperationsToPolicy(formDetails.operations)
      };
      
      onUpdateDetails(modifyDetails);
    };

    if (!isModifyDetails(details)) {
      // If details don't match modify structure, provide defaults
      const defaultModifyDetails: ModifyDetails = {
        roleDefinitionIds: [],
        operations: []
      };
      return (
        <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
          <DetailsForm
            roleDefinitionIds={defaultModifyDetails.roleDefinitionIds}
            conflictEffect={convertConflictEffectToString(defaultModifyDetails.conflictEffect)}
            operations={convertPolicyOperationsToDetailsForm(defaultModifyDetails.operations)}
            onUpdate={handleModifyUpdate}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
        <DetailsForm
          roleDefinitionIds={details.roleDefinitionIds}
          conflictEffect={convertConflictEffectToString(details.conflictEffect)}
          operations={convertPolicyOperationsToDetailsForm(details.operations)}
          onUpdate={handleModifyUpdate}
        />
      </div>
    );
  }

  // Handle auditIfNotExists effect
  if (selectedEffect.toLowerCase() === 'auditifnotexists') {
    if (!isAINEDetails(details)) {
      // If details don't match AINE structure, provide defaults
      const defaultAINEDetails: AINEDetails = {
        type: '',
        existenceCondition: {}
      };
      return (
        <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
          <AINEDetailsForm
            details={defaultAINEDetails}
            onUpdate={onUpdateDetails}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
        <AINEDetailsForm
          details={details}
          onUpdate={onUpdateDetails}
        />
      </div>
    );
  }

  // Handle deployIfNotExists effect
  if (selectedEffect.toLowerCase() === 'deployifnotexists') {
    if (!isDINEDetails(details)) {
      // If details don't match DINE structure, provide defaults
      const defaultDINEDetails: DINEDetails = {
        type: '',
        roleDefinitionIds: [],
        existenceCondition: {},
        deployment: {
          properties: {
            mode: 'Incremental',
            template: {},
            parameters: {}
          }
        }
      };
      return (
        <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
          <DINEDetailsForm
            details={defaultDINEDetails}
            onUpdate={onUpdateDetails}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
        <DINEDetailsForm
          details={details}
          onUpdate={onUpdateDetails}
        />
      </div>
    );
  }

  // Fallback for other effects
  return (
    <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800">
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No Details Available
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Details configuration is not available for the "{selectedEffect}" effect.
        </p>
      </div>
    </div>
  );
}; 